import {useMutation} from '@apollo/react-hooks';

import type {
    AccountUpdateInput,
    AccountInfoFragment as AccountInfo,
    UpdateAccountInfo as UpdateAccountInfoMutation,
    UpdateAccountInfoVariables as UpdateAccountInfoMutationVariables,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../../apollo-client';

import ACCOUNT_INFO_FRAGMENT from './graphql/fragments/account-info-fragment.graphql';
import UPDATE_ACCOUNT_INFO from './graphql/mutations/update-account-info.graphql';

export function useUpdateAccountInfo(): (arg1: AccountUpdateInput) => Promise<any> {
    const [updateAccountInfo] = useMutation<
        UpdateAccountInfoMutation,
        UpdateAccountInfoMutationVariables
    >(UPDATE_ACCOUNT_INFO);

    return (input: AccountUpdateInput) => {
        // Need to read current accountInfo to not blow away current values that are not being
        // updated in this mutation's optimistic response
        const accountInfo = nutApolloClient.readFragment({
            id: input.accountId,
            fragment: ACCOUNT_INFO_FRAGMENT,
            fragmentName: 'AccountInfoFragment',
        });

        return updateAccountInfo({
            variables: {input},
            optimisticResponse: accountInfo
                ? {
                      // @ts-expect-error
                      __typename: 'Mutation',
                      accountUpdate: {
                          __typename: 'AccountUpdatePayload',
                          account: {
                              ...getUpdatedAccountInfoFromInput(input, accountInfo),
                          },
                      },
                  }
                : undefined, // It's worth the wait, no optimistic response if no cached fragment
            update: (proxy, mutationResults) => {
                const updatedAccountInfo =
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.accountUpdate &&
                    mutationResults.data.accountUpdate.account;

                if (!updatedAccountInfo) return;

                proxy.writeFragment({
                    id: input.accountId,
                    fragment: ACCOUNT_INFO_FRAGMENT,
                    fragmentName: 'AccountInfoFragment',
                    data: {
                        ...updatedAccountInfo,
                    },
                });
            },
        });
    };
}

const getUpdatedAccountInfoFromInput = (
    input: AccountUpdateInput,
    accountInfo: AccountInfo
): AccountInfo => {
    const updatedAccountInfo: AccountInfo = {...accountInfo};

    Object.keys(input).forEach((fieldKey) => {
        switch (fieldKey) {
            case 'name':
                // Cannot be null
                if (input.name) {
                    updatedAccountInfo.name = input.name;
                }
                break;
            case 'description':
                updatedAccountInfo.description = input.description || '';
                break;
            case 'territoryId':
                updatedAccountInfo.territory = input.territoryId
                    ? {
                          __typename: 'Territory',
                          id: input.territoryId,
                      }
                    : null;
                break;
            case 'employeeCount':
                updatedAccountInfo.employeeCount =
                    input && input.employeeCount ? input.employeeCount.toString() : '';
                break;
            case 'revenue':
                updatedAccountInfo.revenue = input.revenue
                    ? {
                          __typename: 'EntityAttributeValueCurrency',
                          currencyValue: {
                              __typename: 'Money',
                              amount: input.revenue,
                          },
                      }
                    : null;
                break;
            case 'assigneeId':
                updatedAccountInfo.owner = input.assigneeId
                    ? {
                          id: input.assigneeId,
                          __typename: input.assigneeId.includes('users') ? 'User' : 'Team',
                      }
                    : null;
                break;
            case 'industryId':
                updatedAccountInfo.industry = input.industryId
                    ? {
                          __typename: 'NutshellIndustry',
                          id: input.industryId,
                      }
                    : null;
                break;
            case 'accountTypeId':
                // Cannot be null
                if (input.accountTypeId) {
                    updatedAccountInfo.accountType = {
                        __typename: 'AccountType',
                        id: input.accountTypeId,
                    };
                }
                break;
        }
    });

    return updatedAccountInfo;
};
